// src/api/users.ts
// WHY: Single source of truth for API base; richer error to see URL/status.

export type UpdateUserPayload = {
  displayName?: string;
  brandColor?: string;
  theme?: { primary?: string; secondary?: string; background?: string; text?: string };
  colors?: Record<string, string>;
  __silent?: boolean;
  website?: string;
  socialLinks?: Record<string, string>;
  streamingLinks?: Record<string, string>;
  links?: Array<{ label: string; url: string; icon?: string }>;
  logo?: string;

  // Template selection
  templateId?: string;
  templateMetaId?: string;
  templateKey?: string; // dynamic (server TemplateMeta.id)

  // Account / admin fields
  email?: string;
  password?: string;
  currentPassword?: string;
  isActive?: boolean;

  // Subscription details
  subscription?: {
    type?: 'trial' | 'silver' | 'gold' | 'free' | 'none';
    expiresAt?: string;
    isActive?: boolean;
  };
  /** User's preferred UI language: 'en' | 'sw' | 'fr' */
  preferredLanguage?: string;
};

// API Base Setup
// Prefer same-origin in dev or non-prod to avoid accidental calls to production.
// If an explicit VITE_API_URL is set to a different host than the current page,
// and we're not on that host, fall back to same-origin `/api`.
const explicitOrigin = (import.meta.env.VITE_API_URL || '').replace(/\/+$/, '');
let API_BASE = '/api';
try {
  const currentOrigin = typeof window !== 'undefined' ? window.location.origin.replace(/\/+$/, '') : '';
  // Use explicitOrigin only when it matches currentOrigin host (same deployment) or when currentOrigin is empty (SSR/build time)
  if (explicitOrigin) {
    const expHost = (() => { try { return new URL(explicitOrigin).host; } catch { return ''; } })();
    const curHost = (() => { try { return new URL(currentOrigin || 'http://localhost').host; } catch { return ''; } })();
    if (!curHost || expHost === curHost) {
      API_BASE = `${explicitOrigin}/api`;
    } else {
      API_BASE = '/api';
    }
  }
} catch {
  API_BASE = explicitOrigin ? `${explicitOrigin}/api` : '/api';
}

// ----------- Helpers -----------
function requireId(idOrUsername: string | undefined | null, ctx?: Record<string, unknown>) {
  if (!idOrUsername || typeof idOrUsername !== 'string' || idOrUsername.trim() === '') {
    const err = new Error('updateUser: idOrUsername is missing (got undefined/empty).');
    (err as any).context = ctx;
    throw err;
  }
}

async function toJson(res: Response, url: string) {
  const text = await res.text();
  let data: any = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { raw: text };
  }

  if (!res.ok) {
    const err = new Error(data?.error || data?.message || res.statusText);
    (err as any).status = res.status;
    (err as any).data = data;
    (err as any).url = url;
    throw err;
  }

  return data;
}

// ----------- Update User -----------
export async function updateUser(idOrUsername: string | undefined | null, payload: UpdateUserPayload) {
  requireId(idOrUsername, { payload });
  const key = encodeURIComponent(String(idOrUsername));
  const url = `${API_BASE}/users/${key}`;

  // Attach auth header if available (admin or owner)
  let authHeader: Record<string, string> = {};
  if (typeof window !== 'undefined') {
    try {
      const token =
        localStorage.getItem('dev_admin_token') ||
        localStorage.getItem('owner_token') ||
        '';
      if (token) authHeader = { Authorization: `Bearer ${token}` };
    } catch {
      // ignore localStorage read errors
    }
  }

  // ---------- Clean and Safe Payload ----------
  const cleaned: any = { ...payload };

  // ✅ Handle Template Logic Cleanly (preserve explicit nulls to clear fields on server)
  // Mutual exclusivity:
  // - Selecting dynamic (templateMetaId:string) should clear classic (templateId:null)
  // - Selecting classic (templateId:string) should clear dynamic (templateMetaId:null)
  if (typeof payload.templateMetaId === 'string' && payload.templateMetaId.trim() !== '') {
    cleaned.templateMetaId = payload.templateMetaId;
    // Always clear classic when dynamic is selected
    cleaned.templateId = null as any;
  } else if (typeof payload.templateId === 'string' && payload.templateId.trim() !== '') {
    cleaned.templateId = payload.templateId;
    // Always clear dynamic when classic is selected
    cleaned.templateMetaId = null as any;
  } else {
    // Neither explicitly set. Respect explicit nulls from caller if present; otherwise omit to avoid accidental overwrite.
    if ((payload as any).templateId === null) cleaned.templateId = null as any; else delete cleaned.templateId;
    if ((payload as any).templateMetaId === null) cleaned.templateMetaId = null as any; else delete cleaned.templateMetaId;
  }

  // Dev-only: minimal debug to verify exclusivity/nulls behavior at the network boundary
  if ((import.meta as any).env?.DEV) {
    try {
      // Avoid logging sensitive fields; only log template selectors
      const dbg = {
        templateId: Object.prototype.hasOwnProperty.call(cleaned, 'templateId') ? cleaned.templateId : undefined,
        templateMetaId: Object.prototype.hasOwnProperty.call(cleaned, 'templateMetaId') ? cleaned.templateMetaId : undefined,
      } as Record<string, any>;
      console.debug('[api:updateUser]', { idOrUsername: String(idOrUsername), ...dbg });
    } catch {}
  }

  const r = await fetch(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...authHeader },
    credentials: 'include',
    body: JSON.stringify(cleaned),
  });

  return toJson(r, url);
}

// Optional alias for readability
export async function updateUserByIdOrUsername(
  idOrUsername: string | undefined | null,
  payload: UpdateUserPayload
) {
  return updateUser(idOrUsername, payload);
}

// ----------- Get User -----------
export async function getUser(idOrUsername: string | undefined | null) {
  if (!idOrUsername || typeof idOrUsername !== 'string' || idOrUsername.trim() === '') {
    const err = new Error('getUser: idOrUsername is missing');
    (err as any).context = { idOrUsername };
    throw err;
  }
  // Pre-auth gating: during signup/template-selection flows, avoid unauthenticated lookups
  // that would 404/401 and add noise. Allow when an owner/admin token exists, or when
  // the identifier looks like a database id (24-hex) or an email.
  try {
    const hasOwner = typeof window !== 'undefined' ? !!localStorage.getItem('owner_token') : false;
    const hasAdmin = typeof window !== 'undefined' ? !!localStorage.getItem('dev_admin_token') : false;
    const keyRaw = String(idOrUsername).trim();
    const isObjectId = /^[0-9a-fA-F]{24}$/.test(keyRaw);
    const isEmail = /@/.test(keyRaw);
    const isPublicLookupSafe = isObjectId || isEmail;
    if (!hasOwner && !hasAdmin && !isPublicLookupSafe) {
      const skipErr = new Error('getUser: unauthenticated lookup skipped');
      (skipErr as any).status = 0;
      (skipErr as any).skipped = true;
      (skipErr as any).url = `/api/users/${encodeURIComponent(keyRaw)}`;
      throw skipErr;
    }
  } catch {}
  const key = encodeURIComponent(String(idOrUsername));
  const url = `${API_BASE}/users/${key}`;
  const backoffs = [250, 750]; // ms (2 retries)
  for (let attempt = 0; attempt < backoffs.length + 1; attempt++) {
    try {
      const r = await fetch(url, { credentials: 'include' });
      return await toJson(r, url);
    } catch (e: any) {
      const status = (e && typeof e === 'object') ? (e as any).status : undefined;
      if (status === 503 && attempt < backoffs.length) {
        await new Promise(res => setTimeout(res, backoffs[attempt]));
        continue;
      }
      throw e;
    }
  }
  // Should never reach here
  const r = await fetch(url, { credentials: 'include' });
  return toJson(r, url);
}

// ----------- Delete User -----------
export async function deleteUser(
  idOrUsername: string | undefined | null,
  opts?: { preserveLeads?: boolean }
) {
  requireId(idOrUsername);
  const key = encodeURIComponent(String(idOrUsername));
  const qs = opts?.preserveLeads ? '?preserveLeads=true' : '';
  const url = `${API_BASE}/users/${key}${qs}`;

  const r = await fetch(url, {
    method: 'DELETE',
    credentials: 'include',
  });

  return toJson(r, url);
}
